import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { SectionHeader } from '@/components/home/CoreSections';
import { homeT, milestoneId } from '@/i18n/homeI18n';

const IMAGES = {
  1976: '/generated/timeline-1976.jpg', 1978: '/generated/timeline-1978.jpg', 1990: '/generated/timeline-1990.jpg',
  1994: '/generated/timeline-1994.jpg', 1998: '/generated/timeline-1998.jpg', 2013: '/generated/timeline-2013.jpg',
  2018: '/generated/timeline-2018.jpg', 2021: '/generated/timeline-2021.jpg', 2024: '/generated/timeline-2024.jpg', 2026: '/generated/timeline-2026.jpg',
};

const canHover = () => typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function Milestone({ m, id, flip, last, open, onOpen, onToggle }) {
  return (
    <li
      data-testid={`timeline-${id}`}
      data-state={open ? 'open' : 'closed'}
      onMouseEnter={() => canHover() && onOpen()}
      className="group relative grid grid-cols-[2rem_1fr] lg:grid-cols-[1fr_4rem_1fr] gap-x-4 lg:gap-x-8 items-center"
    >
      <div className={`col-start-2 lg:col-start-auto ${flip ? 'lg:order-3 lg:text-start' : 'lg:order-1 lg:text-end'}`}>
        <button
          type="button"
          data-testid={`timeline-toggle-${id}`}
          aria-expanded={open}
          aria-controls={`timeline-story-${id}`}
          onClick={onToggle}
          className={`block w-[calc(100%+1.5rem)] -mx-3 rounded-xl p-3 text-start transition-colors hover:bg-[#FFF4ED]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EE5A01]/40 ${flip ? 'lg:text-start' : 'lg:text-end'}`}
        >
          <span className={`flex items-baseline gap-3 ${flip ? '' : 'lg:justify-end'}`}>
            <span className={`font-heading font-black text-3xl sm:text-4xl leading-none transition-colors duration-300 ${open ? 'text-[#EE5A01]' : 'text-[#121212]/80 group-hover:text-[#EE5A01]'}`}>{m.year}</span>
            <ChevronDown className={`h-4 w-4 text-[#EE5A01] transition-transform duration-300 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
          </span>
          <span className="mt-3 block font-heading font-bold text-lg text-[#121212] leading-snug">{m.title}</span>
          <span className={`mt-2 block font-body text-sm text-[#666666] leading-relaxed max-w-md ${flip ? '' : 'lg:ms-auto'}`}>{m.desc}</span>
        </button>
        <div id={`timeline-story-${id}`} data-testid={`timeline-story-${id}`} aria-hidden={!open} className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
          <div className="overflow-hidden">
            <div className={`pt-4 max-w-md ${flip ? '' : 'lg:ms-auto'}`}>
              <span className="chip-orange">{m.tag}</span>
              <p className="mt-3 font-body text-sm text-[#444444] leading-relaxed">{m.story}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="col-start-1 row-start-1 row-span-2 lg:col-start-auto lg:row-start-auto lg:row-span-1 lg:order-2 relative flex justify-center self-stretch">
        {!last && <span className="absolute top-1/2 -bottom-12 lg:-bottom-20 w-px bg-black/10" aria-hidden="true" />}
        <span className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-[3px] border-[#EE5A01] transition-all duration-300 ${open ? 'bg-[#EE5A01] shadow-[0_0_0_6px_rgba(238,90,1,0.15)]' : 'bg-white group-hover:bg-[#EE5A01]'}`} aria-hidden="true" />
      </div>

      <div className={`col-start-2 lg:col-start-auto mt-4 lg:mt-0 ${flip ? 'lg:order-1' : 'lg:order-3'}`}>
        <button type="button" onClick={onToggle} aria-label={`${m.year}: ${m.title}`} className={`block w-full overflow-hidden rounded-xl border border-black/[0.06] bg-white transition-[height,transform,box-shadow] duration-500 ease-out ${open ? 'h-56 sm:h-72 lg:h-[380px] lg:scale-[1.03] shadow-[0_24px_60px_rgba(0,0,0,0.14)]' : 'h-44 sm:h-56 lg:h-64 shadow-none'}`}>
          <img src={IMAGES[id]} alt={m.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
        </button>
      </div>
    </li>
  );
}

export function Timeline({ lang = 'en' }) {
  const t = homeT(lang).timeline;
  const [active, setActive] = useState(milestoneId(0));
  return (
    <section data-testid="home-timeline" className="bg-white py-16 sm:py-20 lg:py-24 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-7">
            <SectionHeader compact chip={t.chip} title={t.title} desc={t.desc} />
            <p data-testid="timeline-hint" className="mt-4 inline-flex items-center gap-2 font-heading font-semibold text-xs text-[#EE5A01]"><span className="h-1.5 w-1.5 rounded-full bg-[#EE5A01]" aria-hidden="true" />{t.hint}</p>
          </div>
          <div data-testid="timeline-years" className="lg:col-span-5 flex items-center gap-3 sm:gap-4 lg:justify-end">
            <span className="font-heading font-black text-5xl sm:text-6xl text-[#121212] tracking-tight leading-none">{t.from}</span>
            <span className="h-px w-10 sm:w-16 bg-[#EE5A01]" aria-hidden="true" />
            <span className="font-heading font-black text-5xl sm:text-6xl text-[#EE5A01] tracking-tight leading-none">{t.to}</span>
          </div>
        </div>
        <ol className="mt-14 lg:mt-20 space-y-12 lg:space-y-20">
          {t.milestones.map((m, i) => {
            const id = milestoneId(i);
            return (
              <Milestone key={id} id={id} m={m} flip={i % 2 === 1} last={i === t.milestones.length - 1}
                open={active === id} onOpen={() => setActive(id)} onToggle={() => setActive(active === id ? null : id)} />
            );
          })}
        </ol>
      </div>
    </section>
  );
}
